/** API route constants for chat, conversations, rooms, places and gateway/DO paths. (No `/api/vectorize/*` group: no
 *  flow serves one.) Enforcement adjudication:
 *  NO general route-literal ban. `GATEWAY_ROUTE_PATHS.USER_PREFIX` and `PROPERTY_PREFIX` are the trailing-slash forms of the
 *  `DO_MOUNT_PREFIXES` members, not second SSOTs: the gateway entry matches them with `startsWith`, which needs the separator. */

import { DO_MOUNT_PREFIXES } from '@federa/const/durable-object-routes';
import type { SlashLedRouteTable } from '@federa/types/route-tables';

export const CHAT_API_ROUTES = {
	UPLOAD: '/api/chat/upload',
	PREFIX: '/api/chat/',
} as const satisfies Record<string, `/api/chat/${string}`>;

export const CONVERSATION_API_ROUTES = {
	PREFIX: '/api/conversations/',
} as const satisfies Record<string, `/api/conversations/${string}`>;

/** `CREATE` / `CREATE_GUEST_CHAT` are the two room routes that MINT a caller credential rather than consume one, which
 *  is why the chats entry's room-participant gate exempts them by segment. */
export const ROOM_API_ROUTES = {
	PREFIX: '/api/room/',
	CREATE: '/api/room/create',
	CREATE_GUEST_CHAT: '/api/room/createGuestChat',
} as const satisfies Record<string, `/api/room/${string}`>;

/**
 * Inbound machine-webhook routes on the storage gateway. Each names ONE flow, fixed in code: the flow id is never read
 * from the path or the body, which is what keeps a sender's credential unable to reach any other flow.
 */
export const WEBHOOK_API_ROUTES = {
	/** Zoho CRM's first-caller webhook. Dispatches `caller-intake` and nothing else. The PATH keeps its historical
	 *  `callrail-first-caller` spelling on purpose: it is configured in Zoho's own console, so renaming it would take the
	 *  live integration down for a cosmetic gain. Only the flow it names was renamed. */
	CALLRAIL_FIRST_CALLER: '/api/webhooks/callrail-first-caller',
} as const satisfies Record<string, `/api/webhooks/${string}`>;

export const PLACES_API_ROUTES = {
	AUTOCOMPLETE: '/api/places/autocomplete',
} as const satisfies Record<string, `/api/places/${string}`>;

/** Route paths for the main storage gateway worker (microservices/storage-gateway/flow-engine.ts). */
export const GATEWAY_ROUTE_PATHS = {
	AUCTION_FEED: '/getAuctionFeed',
	/** The PUBLIC property-details route, written in the route-ALIAS grammar: `:property_id` compiles to one path capture,
	 * bound positionally to the flow's snake_case `property_id` input key. The capture name and the input key must stay
	 * identical -- a camelCase key fails the engine's inputSchema gate before the flow ever reaches the DO. */
	PUBLIC_PROPERTY_DETAILS: '/public/property/:property_id',
	USER_PREFIX: `${DO_MOUNT_PREFIXES.USER}/`,
	PROPERTY_PREFIX: `${DO_MOUNT_PREFIXES.PROPERTY}/`,
	/** Auction-feed broadcast hub. No id segment: the shard is derived server-side from the proven subject, never chosen by the caller. */
	FEED_WS: '/feed/ws',
	STRIPE_WEBHOOK_CREATOR: '/stripe/webhook/creator',
} as const satisfies SlashLedRouteTable;

/** The one capture in {@link GATEWAY_ROUTE_PATHS.PUBLIC_PROPERTY_DETAILS}, named once so the builder below and the
 *  route registration cannot disagree about its spelling. */
const PUBLIC_PROPERTY_DETAILS_CAPTURE = ':property_id';

/**
 * @runtime-derived-ssot-helper -- the CONCRETE public property-details path for one id, derived by substituting the
 * single capture in {@link GATEWAY_ROUTE_PATHS.PUBLIC_PROPERTY_DETAILS}.
 *
 * The alias-grammar const is what the gateway REGISTERS; a client needs the filled path, and without a builder the only
 * way to get one was to rebuild the literal by hand -- which `spa/chats` did (`/public/property/${id}`), leaving the
 * public read path spelled in two places with nothing tying them together. Encoding happens HERE rather than at the
 * call site, because forgetting it is the other half of the failure this removes.
 */
export function buildPublicPropertyDetailsPath(propertyId: string): string {
	return GATEWAY_ROUTE_PATHS.PUBLIC_PROPERTY_DETAILS.replace(PUBLIC_PROPERTY_DETAILS_CAPTURE, encodeURIComponent(propertyId));
}

/**
 * The PUBLIC guest-credential mint on the storage gateway (`guest-session-issue`). Unauthenticated by construction --
 * it is the endpoint a signed-out visitor calls to OBTAIN a credential, so requiring one would be circular. It takes no
 * caller-supplied identity: the guest id is generated server-side, which is what stops a caller from asking for a token
 * bound to somebody else's subject.
 */
export const GUEST_SESSION_ISSUE_PATH = '/public/guest-session';
